import * as React from 'react';
import { Range } from 'immutable';
import { useRef, useContext } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CalendarContext } from 'ui-addon-calendars/calendar/CalendarContextProvider';
import { Header, HourRow, HourMarker, HoursWrapper, Spacer, RowWrapper, ContentWrapper, DayColumnWrapper } from './DayWeekLayoutStyles';
import { getFormattedHour, getMomentForWeek, getWeekdaysRange } from '../../lib/time';
import { getScrollbarWidth } from '../../lib/scrollbarSizeUtil';
import CALENDAR_VIEW_TYPES from '../../constants/CalendarViewTypes';
import ScrollToTime from './ScrollToTime';
import { CalendarPropsConsumer } from '../../internal/CalendarPropsProvider';
import { CalendarViewShell } from '../CalendarViewShell';
import { View } from 'foundations-components/components/View';
import { styled, withTheme } from '../../lib/styles';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Flex } from 'foundations-components/components/Flex';
import { LastIcon } from 'foundations-assets/icons/components/LastIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Rotator = styled.div.withConfig({
  displayName: "DayWeekLayoutView__Rotator",
  componentId: "sc-1dx7uyf-0"
})(["rotate:", "deg;transition:rotate 300ms;"], ({
  rotate
}) => rotate);
const UIDayWeekLayout = ({
  view,
  displayYear,
  displayDay,
  hideWeekends,
  timezone,
  theme,
  header,
  showEvents,
  allDayEventsContent,
  showAllDayEvents,
  onToggleAllDayEvents,
  children,
  disableScrollIfPopoverOpen,
  hideHeader,
  scrollToTime,
  scrollOnViewChange,
  overflowPopoverOpen
}) => {
  const scrollingElementRef = useRef(null);
  const {
    hasPopoverOpen
  } = useContext(CalendarContext) || {};
  const scrollbarWidth = getScrollbarWidth();
  return /*#__PURE__*/_jsxs(CalendarViewShell, {
    "data-test-id": `calendar-container--${view}`,
    children: [/*#__PURE__*/_jsx(ScrollToTime, {
      scrollingElementRef: scrollingElementRef,
      time: scrollToTime ? scrollToTime.valueOf() : 0,
      scrollOnViewChange: scrollOnViewChange !== null && scrollOnViewChange !== void 0 ? scrollOnViewChange : false
    }), !hideHeader && /*#__PURE__*/_jsxs(Header, {
      "data-test-id": "calendar-week-header",
      isWeekView: true,
      scrollbarWidth: scrollbarWidth,
      children: [showEvents && /*#__PURE__*/_jsx(Spacer, {}), header]
    }), allDayEventsContent && /*#__PURE__*/_jsxs(RowWrapper, {
      isWeekView: view === CALENDAR_VIEW_TYPES.WEEK,
      scrollbarWidth: scrollbarWidth,
      children: [/*#__PURE__*/_jsx(Spacer, {
        onlyAllDayEvents: !showEvents,
        children: showEvents && /*#__PURE__*/_jsx(Flex, {
          alignItems: "start",
          justifyContent: "end",
          padding: "200",
          inlineSize: "100%",
          children: /*#__PURE__*/_jsx(IconButton, {
            "data-test-id": "all-day-events-toggler",
            onClick: onToggleAllDayEvents,
            use: "unstyled",
            placement: "top",
            tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
              message: showAllDayEvents === 'expanded' ? 'calendar.event.collapseAllDayEvents' : 'calendar.event.expandAllDayEvents'
            }),
            children: /*#__PURE__*/_jsx(Rotator, {
              rotate: showAllDayEvents === 'expanded' ? 90 : -90,
              children: /*#__PURE__*/_jsx(LastIcon, {
                color: "fill-tertiary-pressed"
              })
            })
          })
        })
      }), allDayEventsContent]
    }), showEvents ? /*#__PURE__*/_jsx(ContentWrapper, {
      disableScrollIfPopoverOpen: disableScrollIfPopoverOpen,
      hasPopoverOpen: hasPopoverOpen || overflowPopoverOpen,
      ref: scrollingElementRef,
      children: /*#__PURE__*/_jsxs("div", {
        style: {
          position: 'relative'
        },
        children: [/*#__PURE__*/_jsxs(DayColumnWrapper, {
          children: [/*#__PURE__*/_jsx(Spacer, {}), children]
        }), /*#__PURE__*/_jsx(HoursWrapper, {
          children: /*#__PURE__*/_jsx(CalendarPropsConsumer, {
            children: ({
              onClickTime,
              onMouseMoveTime
            }) => Range(0, 24).map(hour => {
              const range = view === CALENDAR_VIEW_TYPES.WEEK ? getWeekdaysRange(hideWeekends, I18n.moment.locale()) : [0];
              const hourBlocks = range.map(dayOfWeek => {
                const thisMoment = view === CALENDAR_VIEW_TYPES.WEEK ? getMomentForWeek(displayYear, displayDay, dayOfWeek, timezone).hour(hour) : (timezone ? I18n.moment().tz(timezone) : I18n.moment.portalTz()).year(displayYear).dayOfYear(displayDay).hour(hour);
                return /*#__PURE__*/_jsx(View, Object.assign({
                  "data-test-id": `${view}-day-${thisMoment.format('D-M-YYYY-H')}`,
                  borderBlockEndWidth: "100",
                  borderBlockEndStyle: "solid",
                  borderBlockEndColor: "border-core-subtle",
                  borderInlineStartWidth: "100",
                  borderInlineStartStyle: "solid",
                  borderInlineStartColor: "border-core-subtle",
                  flex: "1 1 auto",
                  blockSize: "100%"
                }, hour === 0 && {
                  borderBlockStartWidth: '100',
                  borderBlockStartStyle: 'solid',
                  borderBlockStartColor: 'border-core-subtle'
                }, theme.uiAddonCalendars.util.getTemporalModifiers({
                  forMoment: thisMoment,
                  magnitude: 'hour'
                }), {
                  display: "flex",
                  children: /*#__PURE__*/_jsx("div", {
                    onClick: e => onClickTime(thisMoment, e),
                    onMouseMove: e => onMouseMoveTime(thisMoment, e),
                    style: {
                      flex: '1 1 auto'
                    }
                  })
                }), dayOfWeek);
              });
              return /*#__PURE__*/_jsxs(HourRow, {
                children: [/*#__PURE__*/_jsx(HourMarker, {
                  children: getFormattedHour(hour, timezone)
                }), hourBlocks]
              }, hour);
            })
          })
        })]
      })
    }) : null]
  });
};
export default withTheme(UIDayWeekLayout);